<script lang="ts">
  import { isNativeShell, openAppSettings } from '../../native'
  import { isIos, isStandalone } from '../../pwa.svelte'
  import { places } from '../../state/places.svelte'
  import {
    ALERT_LEVELS,
    disableHere,
    enableHere,
    hereLine,
    push,
    pushStateLine,
    pushSupport,
    sendTest,
    setLevel,
    setRain,
  } from '../../state/push.svelte'
  import Icon from '../Icon.svelte'

  /**
   * Alerts at saved places (Phase 9 plan §9.5): one line of state with what it
   * means, the level, and a test. The bells themselves are on the rows in
   * Places, beside the thing they belong to; this section says what they add
   * up to.
   *
   * The state never calls a browser's refusal a failure of ours: a blocked
   * permission, a Safari tab on an iPhone and Brave's push service being off
   * each get their own sentence, because each has its own fix. Blocked is a
   * dead end the app cannot fix, so it names the path to the site setting and
   * the test is disabled with the reason beside it (round 2's 3.5).
   *
   * Rain starting soon (nowcast Stage C) is one switch for the device, not a
   * second bell per place: it follows the bells, so it needs no permission
   * prompt of its own.
   *
   * In the iOS app there is a third thing to be alerted about: wherever the
   * phone is (ios-app-plan §6.4). It is a switch of its own, the only thing
   * that asks iOS for Always, and it says under it when the server last
   * heard where the phone is, so a gap can be seen rather than guessed at.
   */

  let support = $derived(pushSupport())
  let line = $derived(pushStateLine())
  let on = $derived(places.list.filter((p) => p.alerts).length)
  let native = isNativeShell()
  let hereOn = $derived(native && push.here?.on === true)
  let hereState = $derived(hereLine())
  let hereBlocked = $derived(
    hereOn && push.here !== null && push.here.permission !== 'always'
  )

  async function toggleHere(box: HTMLInputElement): Promise<void> {
    if (box.checked) await enableHere()
    else await disableHere()
    // A refusal leaves the switch where the app says it is, not where the
    // thumb left it.
    box.checked = push.here?.on === true
  }
  let blocked = $derived(support === 'ready' && push.permission === 'denied')
  let allowed = $derived(support === 'ready' && push.permission === 'granted')

  /** Where the switch is, in the browser this is. */
  function unblockPath(): string {
    const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
    if (isIos() && isStandalone()) {
      return 'Fusion WX can’t ask again. Open the iPhone’s Settings, then Notifications, then Fusion WX, and allow notifications.'
    }
    if (/Firefox\//.test(ua)) {
      return 'Fusion WX can’t ask again. Click the icon left of the address bar, clear the blocked Notifications permission, then reload.'
    }
    if (/Safari\//.test(ua) && !/Chrom(e|ium)\//.test(ua)) {
      return 'Fusion WX can’t ask again. In Safari’s Settings, open Websites, then Notifications, set this site to Allow, then reload.'
    }
    return 'Fusion WX can’t ask again. Click the site controls left of the address bar, set Notifications to Allow, then reload.'
  }

  let detail = $derived.by(() => {
    if (support === 'install') {
      return 'On an iPhone, alerts reach the installed app only. Tap Share, then “Add to Home Screen”, and open Fusion WX from there.'
    }
    if (support !== 'ready') return null
    if (blocked) return unblockPath()
    if (places.list.length === 0 && !hereOn) {
      return native
        ? 'Save a place and turn on its bell in Places, or turn on alerts where you are below.'
        : 'Save a place, then turn on its bell in Places.'
    }
    if (on === 0 && !hereOn) {
      return native
        ? 'Turn on the bell beside a place in Places, or alerts where you are below.'
        : 'Turn on the bell beside a place in Places. Alerts follow saved places, not My location.'
    }
    const what = { warnings: 'Warnings', watches: 'Warnings and watches', all: 'Alerts of every kind' }[
      push.level
    ]
    if (on === 0) return `${what} arrive for wherever this iPhone is.`
    const plus = push.rain ? ', and rain starting soon,' : ''
    const where = hereOn ? ', and wherever this iPhone is' : ''
    return `${what}${plus} arrive for the ${on === 1 ? 'place' : `${on} places`} with the bell on${where}.`
  })
</script>

<div class="state" class:blocked>
  <span class="mark">
    {#if blocked}
      <Icon name="bell-off" size={18} strokeWidth={2.5} />
    {:else if allowed && (on > 0 || hereOn)}
      <Icon name="check" size={18} strokeWidth={3} />
    {:else}
      <Icon name="bell" size={18} />
    {/if}
  </span>
  <span class="text">
    <span class="title">{blocked ? (isNativeShell() ? 'Turned off in iOS Settings' : 'Blocked by the browser') : line}</span>
    {#if detail}<span class="detail">{detail}</span>{/if}
  </span>
</div>

{#if support === 'ready'}
  {#if push.problem}<p class="note problem" role="status">{push.problem}.</p>{/if}

  {#if native}
    <label class="option rain touch-target">
      <input
        class="wx-box"
        type="checkbox"
        checked={hereOn}
        disabled={push.busy}
        onchange={(e) => void toggleHere(e.currentTarget)}
      />
      <span class="rain-text">
        Alerts where I am
        <span class="hint">
          Wherever this iPhone is, even with the app closed. The app tells the server where it is, to
          about a kilometre, and the server forgets it after a day without hearing again.
        </span>
        {#if hereState}<span class="hint here-state" class:warn={hereBlocked}>{hereState}.</span>{/if}
      </span>
    </label>
    {#if hereBlocked}
      <div class="test">
        <button type="button" class="wx-btn touch-target" onclick={openAppSettings}>Open iOS Settings</button>
      </div>
    {/if}
  {/if}

  <div class="levels" role="radiogroup" aria-label="Alert me about">
    <span class="label">Alert me about</span>
    {#each ALERT_LEVELS as level (level.id)}
      <label class="option touch-target">
        <input
          class="wx-box"
          type="radio"
          name="alert-level"
          value={level.id}
          checked={push.level === level.id}
          onchange={() => setLevel(level.id)}
        />
        {level.label}
      </label>
    {/each}
  </div>

  <label class="option rain touch-target">
    <input
      class="wx-box"
      type="checkbox"
      checked={push.rain}
      onchange={(e) => setRain(e.currentTarget.checked)}
    />
    <span class="rain-text">
      Rain starting soon
      <span class="hint">From the radar, up to 15 minutes ahead. At most once an hour per place.</span>
    </span>
  </label>

  <div class="test">
    <button
      type="button"
      class="wx-btn touch-target"
      class:ink={push.subscribed && !push.busy}
      aria-label="Send a test notification"
      disabled={!push.subscribed || push.busy}
      onclick={() => void sendTest()}>Send a test</button
    >
    {#if blocked}
      <span class="why">Needs permission first</span>
    {:else if !push.subscribed}
      <span class="why">{native ? 'Turn on a bell or alerts where I am first' : 'Turn on a bell first'}</span>
    {/if}
  </div>
  {#if push.test}
    <p class="note" role="status">
      <b>{push.test}.</b>
      {#if push.test.startsWith('Sent')}
        <span class="muted">Nothing arrived? Check Focus or Do Not Disturb.</span>
      {/if}
    </p>
  {/if}
  {#if blocked && on > 0}
    <p class="note muted">
      Bells stay on for {on === 1 ? 'one place' : `${on} places`} and start working once allowed.
    </p>
  {/if}
{/if}

<style>
  .state {
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr);
    gap: 10px;
    padding: 12px 0;
    border-bottom: 1px solid var(--color-divider);
  }

  .mark {
    padding-top: 1px;
  }

  .state:not(.blocked) .mark :global([data-icon='check']) {
    color: var(--color-success);
  }

  .blocked .mark,
  .blocked .title {
    color: var(--color-warn);
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .title {
    font-size: 13px;
    font-weight: 800;
  }

  .detail {
    font-size: 12px;
    line-height: 1.45;
  }

  .levels,
  .test,
  .note {
    margin-left: 30px;
  }

  .levels {
    display: flex;
    flex-direction: column;
    padding-top: 10px;
  }

  .label {
    font-size: 12px;
    color: var(--color-muted);
    margin-bottom: 2px;
  }

  .option {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 30px;
    font-size: 13px;
    cursor: pointer;
  }

  .rain {
    margin-left: 30px;
    padding-top: 10px;
    align-items: flex-start;
  }

  .rain .wx-box {
    margin-top: 1px;
  }

  .rain-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .hint {
    font-size: 11px;
    line-height: 1.4;
    color: var(--color-muted);
  }

  .here-state {
    color: var(--color-text);
  }

  .here-state.warn {
    color: var(--color-warn);
  }

  .test {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-top: 12px;
  }

  .why {
    font-size: 11px;
    color: var(--color-muted);
  }

  .note {
    margin-top: 10px;
    margin-bottom: 0;
    font-size: 12px;
    line-height: 1.45;
  }

  .note b {
    font-weight: 800;
  }

  .problem {
    color: var(--color-warn);
  }
</style>
